import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Typography,
} from "@material-ui/core";
import { css } from "@emotion/css";
import PollingImage from "shared/PollingImage";
import { LocalizedText } from "../types";
import { getErrorMessage, readLocale, writeLocale } from "../utils";
import { DEFAULT_LOCALE, LOCALES, LocaleMeta } from "../constants";
import { useUploadMedia } from "routes/createContent/api/useUploadMedia";

type UploadRowProps = {
  src: string | undefined;
  uploading: boolean;
  disabled?: boolean;
  onPickFile: (file: File) => void;
  onRemove: () => void;
  label?: React.ReactNode;
};

/** Preview + Upload/Change button + Remove button. Shared between the generic
 *  and per-locale layouts. */
const UploadRow: React.VFC<UploadRowProps> = ({ src, uploading, disabled, onPickFile, onRemove, label }) => (
  <Box className={styles.localeRow}>
    {label && (
      <Typography variant="caption" className={styles.localeTag}>
        {label}
      </Typography>
    )}
    {src && <PollingImage src={src} className={styles.preview} />}
    <Box>
      <Button
        variant="outlined"
        component="label"
        size="small"
        disabled={disabled || uploading}
        startIcon={uploading ? <CircularProgress size={14} /> : undefined}
      >
        {src ? "Change" : "Upload"}
        <input
          type="file"
          accept="image/*"
          hidden
          onChange={e => {
            if (e.target.files?.[0]) onPickFile(e.target.files[0]);
          }}
        />
      </Button>
      {src && (
        <Button size="small" onClick={onRemove}>
          Remove
        </Button>
      )}
    </Box>
  </Box>
);

type Props = {
  value: LocalizedText;
  onChange: (value: LocalizedText) => void;
  label?: string;
  disabled?: boolean;
};

/** True when more than one distinct URL is set across the locales. */
const hasLocaleSpecificImages = (value: LocalizedText): boolean => {
  const urls = new Set(LOCALES.map(l => value[l.code]).filter(Boolean));
  return urls.size > 1;
};

type ImageMode = "generic" | "locale";

const MediaUploadField: React.VFC<Props> = ({ value, onChange, label = "Image", disabled }) => {
  const upload = useUploadMedia();
  const [uploading, setUploading] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [confirmGeneric, setConfirmGeneric] = useState(false);

  // Derive initial mode from data: if any translation has a distinct URL, start in locale mode.
  const [mode, setMode] = useState<ImageMode>(() => (hasLocaleSpecificImages(value) ? "locale" : "generic"));

  const handleCheckboxChange = (checked: boolean) => {
    if (!checked && hasLocaleSpecificImages(value)) {
      setConfirmGeneric(true);
      return;
    }
    setMode(checked ? "locale" : "generic");
  };

  /** Set every locale to the same URL (or clear all when url is ""). */
  const writeAllLocales = (url: string): LocalizedText =>
    LOCALES.reduce((acc, l) => writeLocale(acc, l.code, url), {} as LocalizedText);

  const confirmSwitchToGeneric = () => {
    // Prefer the default-locale URL, but fall back to any non-empty URL so the
    // user doesn't lose all images when the default locale happens to be empty.
    const fallback = LOCALES.map(l => value[l.code]).find(Boolean) ?? "";
    onChange(writeAllLocales(value[DEFAULT_LOCALE] || fallback));
    setMode("generic");
    setConfirmGeneric(false);
  };

  const handleUpload = async (code: string, file: File) => {
    setUploading(code);
    setUploadError(null);
    try {
      const url = await upload.mutateAsync(file);
      onChange(code === "generic" ? writeAllLocales(url) : writeLocale(value, code, url));
    } catch (error) {
      setUploadError(getErrorMessage(error));
    } finally {
      setUploading(null);
    }
  };

  return (
    <Box className={styles.wrap}>
      <Typography variant="subtitle2">{label}</Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={mode === "locale"}
            onChange={e => handleCheckboxChange(e.target.checked)}
            size="small"
          />
        }
        label="Language specific images"
      />

      {mode === "generic" ? (
        <UploadRow
          src={value[DEFAULT_LOCALE] || undefined}
          uploading={uploading === "generic"}
          disabled={disabled}
          onPickFile={file => handleUpload("generic", file)}
          onRemove={() => onChange(writeAllLocales(""))}
        />
      ) : (
        <Box className={styles.translations}>
          {LOCALES.map((meta: LocaleMeta) => (
            <UploadRow
              key={meta.code}
              src={readLocale(value, meta.code) || undefined}
              uploading={uploading === meta.code}
              disabled={disabled}
              onPickFile={file => handleUpload(meta.code, file)}
              onRemove={() => onChange(writeLocale(value, meta.code, ""))}
              label={
                <>
                  {meta.flag} {meta.label}
                </>
              }
            />
          ))}
        </Box>
      )}

      {uploadError && (
        <Box mt={1} color="error.main">
          {uploadError}
        </Box>
      )}

      <Dialog open={confirmGeneric} onClose={() => setConfirmGeneric(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Switch to generic image?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Some locales have language-specific images. Switching to generic will replace all
            locale images with the default-locale image. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmGeneric(false)}>Cancel</Button>
          <Button color="secondary" variant="contained" onClick={confirmSwitchToGeneric}>
            Switch to generic
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MediaUploadField;

const styles = {
  wrap: css`
    margin: 12px 0;
  `,
  localeRow: css`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 8px;
  `,
  localeTag: css`
    min-width: 100px;
  `,
  preview: css`
    max-height: 60px;
    max-width: 100px;
    object-fit: cover;
    border: 1px solid #e0e0e0;
  `,
  translations: css`
    margin-top: 4px;
    padding-left: 16px;
    border-left: 2px solid #e0e0e0;
  `,
};
